// Helpers de tempo localizados para o inbox.
// `formatChatTime` → label curto pra lista de conversas ("agora", "5min", "1h", "ontem", "23/05").
// `formatBubbleTime` → "HH:mm" pra bolhas.
// `formatTimelineStamp` → "16/07 09:12" pros marcos da timeline de status.
import { differenceInMinutes, differenceInHours, format, isToday, isYesterday, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';

function toDate(input: Date | string): Date | null {
  const d = typeof input === 'string' ? parseISO(input) : input;
  return isValid(d) ? d : null;
}

export function formatChatTime(input: Date | string, now: Date = new Date()): string {
  const d = toDate(input);
  if (!d) return '';

  const minutes = differenceInMinutes(now, d);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes}min`;

  const hours = differenceInHours(now, d);
  if (isToday(d)) return `${hours}h`;
  if (isYesterday(d)) return 'ontem';

  return format(d, 'dd/MM', { locale: ptBR });
}

export function formatBubbleTime(input: Date | string): string {
  const d = toDate(input);
  return d ? format(d, 'HH:mm', { locale: ptBR }) : '';
}

// Marco da timeline de status (RF-08). Data + hora, porque um atendimento atravessa
// dias e só "HH:mm" não situa o marco. O ano só entra quando não é o corrente.
export function formatTimelineStamp(input: Date | string, now: Date = new Date()): string {
  const d = toDate(input);
  if (!d) return '';
  const pattern = d.getFullYear() === now.getFullYear() ? 'dd/MM HH:mm' : 'dd/MM/yyyy HH:mm';
  return format(d, pattern, { locale: ptBR });
}

/**
 * Instante que SITUA O DIA: "hoje às 18:46", "ontem às 23:30", "12/08 às 03:19".
 *
 * 🔴 Nasceu de um defeito de leitura: o card da Fila mostrava `formatBubbleTime`
 * ("Chegou às 23:30"), o util das BOLHAS — onde o dia vem do separador da timeline.
 * Num card solto não há separador, e "23:30" é indistinguível de hoje, de ontem e
 * de três meses atrás. Numa conversa importada, que nasce datada no passado, isso
 * fazia o card parecer estar mostrando dois dados conflitantes.
 *
 * Diferente de `formatTimelineStamp` ("12/08 03:19"), aqui o dia recente vira
 * palavra: quem lê a fila decide pelo "ontem", não por converter data mentalmente.
 */
export function formatMomentWithDay(input: Date | string, now: Date = new Date()): string {
  const d = toDate(input);
  if (!d) return '';
  const hora = format(d, 'HH:mm', { locale: ptBR });
  if (isToday(d)) return `hoje às ${hora}`;
  if (isYesterday(d)) return `ontem às ${hora}`;
  const dia =
    d.getFullYear() === now.getFullYear()
      ? format(d, 'dd/MM', { locale: ptBR })
      : format(d, 'dd/MM/yyyy', { locale: ptBR });
  return `${dia} às ${hora}`;
}

export function formatDayDivider(input: Date | string): string {
  const d = toDate(input);
  if (!d) return '';
  if (isToday(d)) return `Hoje · ${format(d, "d 'de' MMMM", { locale: ptBR })}`;
  if (isYesterday(d)) return `Ontem · ${format(d, "d 'de' MMMM", { locale: ptBR })}`;
  return format(d, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
}
